import { EAnalyticsEvent } from "@frontend-shared/product-analytics";
import { useCallback, useState } from "react";

import { useProductAnalytics } from "@/hooks/use-product-analytics";
import type {
  IResetTourProps,
  IUseTourLifecycleProps,
  IUseTourLifecycleReturn,
} from "@/providers/types";

export const useTourLifecycle = ({
  tour,
  isCompleted,
  completeTour,
  onStartTour,
  onResetTour,
  onWelcomeClose,
}: IUseTourLifecycleProps): IUseTourLifecycleReturn => {
  const { capture } = useProductAnalytics();
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const startTour = useCallback((): void => {
    onStartTour?.();
    setIsRunning(false);
    setCurrentStep(0);
    setIsWelcomeOpen(true);
  }, [onStartTour]);

  const handleStartActualTour = useCallback((): void => {
    // NOTE: NSAI-4615 — the tour actually begins here, not when the welcome
    // dialog opens. Firing on the dialog would count everyone who was merely
    // offered the tour as having started it.
    capture({
      event: EAnalyticsEvent.ONBOARDING_TOUR_STARTED,
      properties: { tour },
    });
    setIsWelcomeOpen(false);
    setCurrentStep(0);
    setIsRunning(true);
  }, [capture, tour]);

  const resetTour = useCallback(
    (props?: IResetTourProps): void => {
      // NOTE: NSAI-4615 — `resetTour` is the single exit path for both a
      // finished tour and an abandoned one, so the caller's verdict decides
      // which event fires. Absent verdict means a programmatic reset (a
      // provider tearing the tour down), which is neither.
      if (props) {
        capture({
          event: props.isCompleted
            ? EAnalyticsEvent.ONBOARDING_TOUR_COMPLETED
            : EAnalyticsEvent.ONBOARDING_TOUR_SKIPPED,
          properties: { tour, step_index: currentStep },
        });
      }

      // NOTE: Fire the mutation without awaiting so its optimistic completion
      // update batches with the local state changes — prevents the auto-fire
      // effect from racing back in and re-opening the dialog.
      if (!isCompleted) {
        completeTour();
      }
      setIsRunning(false);
      onResetTour?.();
      setCurrentStep(0);
    },
    [capture, currentStep, isCompleted, completeTour, onResetTour, tour],
  );

  const handleWelcomeClose = useCallback((): void => {
    // NOTE: NSAI-4615 — dismissed at the welcome dialog, before step 1. Counts
    // as skipped at index 0, which is what separates "never wanted the tour"
    // from "gave up partway through".
    capture({
      event: EAnalyticsEvent.ONBOARDING_TOUR_SKIPPED,
      properties: { tour, step_index: 0 },
    });
    onWelcomeClose?.();
    if (!isCompleted) {
      completeTour();
    }
    setIsWelcomeOpen(false);
  }, [capture, isCompleted, completeTour, onWelcomeClose, tour]);

  return {
    isWelcomeOpen,
    setIsWelcomeOpen,
    isRunning,
    setIsRunning,
    currentStep,
    setCurrentStep,
    startTour,
    handleStartActualTour,
    resetTour,
    handleWelcomeClose,
  };
};
